import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Typography as MuiTypography,
  useTheme as useMuiTheme,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CancelIcon from '@mui/icons-material/Cancel'
import { useTheme } from '@/contexts/ThemeContext'
import { formatAddress, formatHashShort, formatDate } from '@/utils/formatting'
import { getContractName } from './utils'
import type { PendingTransaction } from './types'

interface PendingTransactionCardProps {
  transaction: PendingTransaction
  userAddress: string | undefined
  treasurySafe: string | undefined
  stngProxy: string | undefined
  operationalSafeFactory: string | undefined
  isMobileOrTablet: boolean
  safeThreshold: number | null
  signaturesCount: number
  hasUserSigned: boolean
  isSigning: boolean
  isExecuting: boolean
  isRejecting: boolean
  isConfirmingThisTx: boolean
  canExecute: boolean
  canReject: boolean
  showOverlay: boolean
  simulationResult?: { success: boolean; error?: string }
  isSimulating: boolean
  onSign: () => void
  onExecute: () => void
  onSimulate: () => void
  onReject: () => void
  onCopyHash: (hash: string) => void
  isSafeReady: boolean
  userOperationalSafes: Set<string>
}

export default function PendingTransactionCard({
  transaction,
  treasurySafe,
  stngProxy,
  operationalSafeFactory,
  isMobileOrTablet,
  safeThreshold,
  signaturesCount,
  hasUserSigned,
  isSigning,
  isExecuting,
  isRejecting,
  isConfirmingThisTx,
  canExecute,
  canReject,
  showOverlay,
  simulationResult,
  isSimulating,
  onSign,
  onExecute,
  onSimulate,
  onReject,
  onCopyHash,
  isSafeReady,
  userOperationalSafes,
}: PendingTransactionCardProps) {
  const { colors } = useTheme()
  const muiTheme = useMuiTheme()
  const { tx, hash, type, safeAddress } = transaction

  if (!safeAddress) return null

  const isTreasurySafe =
    safeAddress.toLowerCase() === treasurySafe?.toLowerCase()

  const isSignDisabled =
    hasUserSigned ||
    isSigning ||
    isExecuting ||
    isRejecting ||
    (isTreasurySafe && !isSafeReady) ||
    (!isTreasurySafe && !userOperationalSafes.has(safeAddress.toLowerCase()))

  const isExecuteDisabled =
    !canExecute ||
    isExecuting ||
    isSigning ||
    isRejecting ||
    isConfirmingThisTx ||
    (isTreasurySafe && !isSafeReady) ||
    (!isTreasurySafe && !userOperationalSafes.has(safeAddress.toLowerCase()))

  const isSimulateDisabled =
    !canExecute || isSimulating || isExecuting || isSigning || isRejecting

  const mobileButtonStyles = {
    [muiTheme.breakpoints.down('sm')]: {
      flex: '1 1 calc(50% - 4px)',
      minWidth: 'calc(50% - 4px)',
    },
  }

  return (
    <Card
      sx={{
        mb: 2,
        backgroundColor: colors.overlay,
        border: `1px solid ${colors.primary}`,
        position: 'relative',
      }}
    >
      {showOverlay && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
            borderRadius: '4px',
          }}
        >
          <CircularProgress
            size={40}
            thickness={4}
            sx={{
              color: colors.primary,
            }}
          />
        </Box>
      )}
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {type && (
              <Chip
                label={type}
                size="small"
                sx={{
                  backgroundColor: colors.primary,
                  color: colors.background,
                  fontWeight: 600,
                }}
              />
            )}
            <MuiTypography
              variant="body2"
              sx={{
                color: colors.text,
                fontFamily: 'monospace',
                wordBreak: 'break-all',
              }}
            >
              Hash: {isMobileOrTablet ? formatHashShort(hash) : hash}
            </MuiTypography>
          </Box>
          <IconButton
            size="small"
            onClick={() => onCopyHash(hash)}
            sx={{ color: colors.primary }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
          }}
        >
          <Chip
            label={`Nonce: ${tx.data.nonce}`}
            size="small"
            sx={{
              backgroundColor: colors.overlay,
              color: colors.text,
            }}
          />
          <Chip
            label={`Signatures: ${signaturesCount}${
              safeThreshold !== null ? `/${safeThreshold}` : ''
            }`}
            size="small"
            sx={{
              backgroundColor: colors.overlay,
              color: colors.text,
            }}
          />
          {hasUserSigned && (
            <Chip
              label="You signed"
              size="small"
              sx={{
                backgroundColor: colors.primary,
                color: colors.background,
              }}
            />
          )}
          {simulationResult && (
            <Chip
              label={
                simulationResult.success
                  ? 'Simulation success'
                  : 'Simulation fail'
              }
              size="small"
              sx={{
                backgroundColor: simulationResult.success
                  ? colors.green
                  : colors.error,
                color: colors.background,
              }}
            />
          )}
          {isExecuting || isConfirmingThisTx ? (
            <Chip
              icon={
                <CircularProgress
                  size={14}
                  thickness={5}
                  sx={{
                    color: `${colors.primary} !important`,
                    '& .MuiCircularProgress-circle': {
                      stroke: colors.primary,
                    },
                  }}
                />
              }
              label="Executing"
              size="small"
              sx={{
                backgroundColor: colors.secondary,
                color: colors.background,
                '& .MuiChip-icon': {
                  color: `${colors.primary} !important`,
                },
              }}
            />
          ) : canExecute ? (
            <Chip
              label="Ready to execute"
              size="small"
              sx={{
                backgroundColor: colors.secondary,
                color: colors.background,
              }}
            />
          ) : (
            <Chip
              label="Pending"
              size="small"
              sx={{
                backgroundColor: colors.yellow,
                color: colors.background,
              }}
            />
          )}
        </Box>
        <MuiTypography
          variant="caption"
          sx={{
            color: colors.text,
            opacity: 0.7,
            mb: 1,
            display: 'block',
            fontFamily: 'monospace',
            wordBreak: 'break-all',
          }}
        >
          From: {isMobileOrTablet ? formatAddress(safeAddress) : safeAddress}
        </MuiTypography>
        {tx.data.to && (
          <MuiTypography
            variant="caption"
            sx={{
              color: colors.text,
              opacity: 0.7,
              mb: 1,
              display: 'block',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
            }}
          >
            To:{' '}
            {getContractName(
              tx.data.to,
              stngProxy,
              treasurySafe,
              operationalSafeFactory
            )}{' '}
            ({isMobileOrTablet ? formatAddress(tx.data.to) : tx.data.to})
          </MuiTypography>
        )}
        {transaction.createdDate && (
          <MuiTypography
            variant="caption"
            sx={{
              color: colors.text,
              opacity: 0.7,
              mb: 2,
              display: 'block',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
            }}
          >
            Created: {formatDate(transaction.createdDate)}
          </MuiTypography>
        )}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={onSign}
            disabled={isSignDisabled}
            sx={{
              borderColor: colors.primary,
              color: colors.primary,
              ...mobileButtonStyles,
              '&:hover': {
                borderColor: colors.primary,
                backgroundColor: `${colors.primary}20`,
              },
              '&.Mui-disabled': {
                borderColor: `${colors.primary}40`,
                color: `${colors.primary}80`,
              },
            }}
          >
            {isSigning ? 'Signing...' : 'Sign'}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={onExecute}
            disabled={isExecuteDisabled}
            sx={{
              backgroundColor: colors.secondary,
              color: colors.background,
              fontWeight: 600,
              ...mobileButtonStyles,
              '&:hover': {
                backgroundColor: colors.secondary,
                opacity: 0.9,
              },
              '&.Mui-disabled': {
                backgroundColor: `${colors.secondary}40`,
                color: `${colors.background}80`,
              },
            }}
          >
            {isExecuting || isConfirmingThisTx ? 'Executing...' : 'Execute'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onSimulate}
            disabled={isSimulateDisabled}
            sx={{
              borderColor: colors.green,
              color: colors.green,
              ...mobileButtonStyles,
              '&:hover': {
                borderColor: colors.green,
                backgroundColor: `${colors.green}20`,
              },
              '&.Mui-disabled': {
                borderColor: `${colors.green}40`,
                color: `${colors.green}80`,
              },
            }}
          >
            {isSimulating ? 'Simulating...' : 'Simulate'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CancelIcon />}
            onClick={onReject}
            disabled={!canReject}
            sx={{
              borderColor: colors.error,
              color: colors.error,
              ...mobileButtonStyles,
              '&:hover': {
                borderColor: colors.error,
                backgroundColor: `${colors.error}20`,
              },
              '&.Mui-disabled': {
                borderColor: `${colors.error}40`,
                color: `${colors.error}80`,
              },
            }}
          >
            {isRejecting ? 'Rejecting...' : 'Reject'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}
